import { useCallback, useEffect, useMemo, useState } from 'react';
import { toggleId } from '../utils/photoSelection';
import type { Selection } from '../components/journal/photoSelectionContext';

/** The tick on a photo starts it; while there is a selection a click toggles instead of opening. */
export function usePhotoSelectionState() {
  const [ids, setIds] = useState<Set<string>>(new Set());
  const selecting = ids.size > 0;

  const clear = useCallback(() => setIds(new Set()), []);
  const toggle = useCallback((id: string) => setIds(prev => toggleId(prev, id)), []);
  const selectAll = useCallback((all: string[]) => setIds(new Set(all)), []);
  /** Photos that no longer exist leave the selection. */
  const keepOnly = useCallback((existing: ReadonlySet<string>) => {
    setIds(prev => {
      const next = new Set([...prev].filter(id => existing.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, []);

  useEffect(() => {
    if (!selecting) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') clear(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selecting, clear]);

  const context = useMemo<Selection>(
    () => ({ selecting, isSelected: id => ids.has(id), toggle }),
    [selecting, ids, toggle],
  );
  return { ids, selecting, clear, selectAll, keepOnly, context };
}
